import { useState, useEffect, useCallback } from "react";
import { Plus, PencilSimple, Trash, Buildings, ArrowsClockwise, SealCheck } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import ListingImage from "@/components/ListingImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { REGIONS, PROPERTY_TYPES, PROPERTY_STATUS, formatPrice } from "@/lib/constants";

const EMPTY = {
  title: "", address: "", region: REGIONS[0], price: "", property_type: PROPERTY_TYPES[0],
  bedrooms: "", bathrooms: "", sqft: "", description: "", features: "", images: "", tour_url: "", status: "active",
};

const inputCls = "w-full bg-[#0B0C10] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50";

function PropertyDialog({ open, onClose, editing, onSaved, defaultStatus = "active" }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isNew = !editing?.id;

  useEffect(() => {
    if (editing?.id) {
      setForm({
        ...editing,
        price: String(editing.price ?? ""),
        bedrooms: String(editing.bedrooms ?? ""),
        bathrooms: String(editing.bathrooms ?? ""),
        sqft: editing.sqft != null ? String(editing.sqft) : "",
        features: (editing.features || []).join(", "),
        images: (editing.images || []).join("\n"),
      });
    } else {
      setForm({ ...EMPTY, status: defaultStatus });
    }
  }, [editing, open, defaultStatus]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const save = async () => {
    if (!form.title || !form.address || !form.price) {
      toast.error("Title, address and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      address: form.address,
      region: form.region,
      price: parseInt(form.price, 10) || 0,
      property_type: form.property_type,
      bedrooms: parseInt(form.bedrooms, 10) || 0,
      bathrooms: parseFloat(form.bathrooms) || 1,
      sqft: form.sqft ? parseInt(form.sqft, 10) : null,
      description: form.description,
      features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.images.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      tour_url: form.tour_url?.trim() || null,
      status: form.status,
      source: "curated",
    };
    try {
      if (editing) await api.patch(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing ? "Listing updated" : "Listing added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#101116] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {!isNew ? "Edit listing" : defaultStatus === "sold" ? "Add a sold closing" : "Add a listing"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {!isNew
              ? "Update this listing's details."
              : defaultStatus === "sold"
                ? "Only add homes you actually closed. These appear on /sold."
                : "Add a property you represent. Borough feed homes stay separate."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="lux-label">Title</label>
            <input data-testid="prop-title" className={inputCls} value={form.title} onChange={set("title")} placeholder="Sunlit 2BR Co-op…" />
          </div>
          <div>
            <label className="lux-label">Address</label>
            <input data-testid="prop-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="123 Main St, Bronx, NY 10475" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="lux-label">Region</label>
              <Select value={form.region} onValueChange={set("region")}>
                <SelectTrigger data-testid="prop-region" className="bg-[#0B0C10] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#101116] border-white/10 text-white">
                  {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="lux-label">Type</label>
              <Select value={form.property_type} onValueChange={set("property_type")}>
                <SelectTrigger data-testid="prop-type" className="bg-[#0B0C10] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#101116] border-white/10 text-white">
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className="lux-label">Price $</label><input data-testid="prop-price" type="number" className={inputCls} value={form.price} onChange={set("price")} /></div>
            <div><label className="lux-label">Beds</label><input data-testid="prop-beds" type="number" className={inputCls} value={form.bedrooms} onChange={set("bedrooms")} /></div>
            <div><label className="lux-label">Baths</label><input data-testid="prop-baths" type="number" step="0.5" className={inputCls} value={form.bathrooms} onChange={set("bathrooms")} /></div>
            <div><label className="lux-label">Sqft</label><input data-testid="prop-sqft" type="number" className={inputCls} value={form.sqft} onChange={set("sqft")} /></div>
          </div>
          <div>
            <label className="lux-label">Description</label>
            <textarea data-testid="prop-description" rows={3} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} />
          </div>
          <div>
            <label className="lux-label">Features (comma-separated)</label>
            <input data-testid="prop-features" className={inputCls} value={form.features} onChange={set("features")} placeholder="Updated kitchen, Parking, Backyard" />
          </div>
          <div>
            <label className="lux-label">Image URLs (one per line)</label>
            <textarea data-testid="prop-images" rows={2} className={`${inputCls} resize-none`} value={form.images} onChange={set("images")} placeholder="https://…" />
          </div>
          <div>
            <label className="lux-label">Virtual tour URL (Matterport / YouTube — optional)</label>
            <input data-testid="prop-tour-url" className={inputCls} value={form.tour_url} onChange={set("tour_url")} placeholder="https://my.matterport.com/show/?m=… or YouTube link" />
          </div>
          <div>
            <label className="lux-label">Status</label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="prop-status" className="bg-[#0B0C10] border-white/10 text-white capitalize w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#101116] border-white/10 text-white">
                {PROPERTY_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-white/15 text-sm text-white/70 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="prop-save" className="px-6 py-2.5 rounded-sm bg-brand text-black text-sm uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60">
            {saving ? "Saving…" : !isNew ? "Update" : defaultStatus === "sold" ? "Add sold" : "Add listing"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultStatus, setDefaultStatus] = useState("active");
  const [tab, setTab] = useState("mine");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const params = tab === "sold"
      ? { status: "sold", source: "curated", limit: 200 }
      : { status: "all", source: "curated", limit: 200 };
    const { data } = await api.get("/properties", { params });
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [tab]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/properties/${p.id}`);
    toast.success("Listing deleted");
    load();
  };

  const markSold = async (p) => {
    if (String(p.id || "").startsWith("seed-")) {
      toast.error("That's a sample listing, not a closing. Use Add sold with the real address.");
      return;
    }
    const prev = p.status;
    try {
      await api.patch(`/properties/${p.id}`, { status: "sold" });
      toast.success("Published on /sold", {
        action: {
          label: "Undo",
          onClick: async () => {
            await api.patch(`/properties/${p.id}`, { status: prev || "active" });
            load();
          },
        },
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const refreshFeed = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-listings", null, { params: { force: true } });
      toast.success("Borough inventory refresh started");
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const visible = tab === "sold" ? items.filter((p) => p.status === "sold") : items;

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl text-white">Properties</h1>
          <p className="text-white/50 text-sm mt-1">
            Your curated listings only — not the 7k Borough feed. Sold rows appear on /sold.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={refreshFeed}
            disabled={syncing}
            data-testid="refresh-listings-btn"
            className="flex items-center gap-2 px-4 py-3 rounded-sm border border-white/15 text-sm text-white/70 hover:text-white disabled:opacity-60"
          >
            <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Refreshing…" : "Refresh inventory"}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(null); setDefaultStatus("sold"); setDialogOpen(true); }}
            data-testid="add-sold-btn"
            className="flex items-center gap-2 px-4 py-3 rounded-sm border border-brand/40 text-sm text-brand hover:bg-brand/10"
          >
            <SealCheck size={16} /> Add sold
          </button>
          <button
            type="button"
            onClick={() => { setEditing(null); setDefaultStatus("active"); setDialogOpen(true); }}
            data-testid="add-property-btn"
            className="flex items-center gap-2 px-5 py-3 rounded-sm bg-brand text-black text-sm uppercase tracking-wider font-medium hover:bg-brand-hover transition-colors"
          >
            <Plus size={16} weight="bold" /> Add listing
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        {[
          { id: "mine", label: "My listings" },
          { id: "sold", label: "Sold / closed" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`properties-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider ${
              tab === t.id ? "bg-brand/15 text-brand" : "text-white/50 border border-white/10"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/40 text-sm">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="bg-[#101116] border border-white/5 rounded-sm py-16 text-center">
          <Buildings size={40} weight="duotone" className="mx-auto text-brand/50 mb-4" />
          <p className="text-white/50" data-testid="properties-empty">
            {tab === "sold"
              ? "No closed transactions yet. Add one only if you actually closed it."
              : "No curated listings yet. Add a home you represent — or use Add sold after a closing."}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((p) => (
            <div key={p.id} data-testid={`property-manage-${p.id}`} className="bg-[#101116] border border-white/5 rounded-sm overflow-hidden">
              <div className="relative aspect-[16/10]">
                <ListingImage
                  src={p.images?.[0]}
                  alt={p.title}
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-3 left-3 px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm bg-black/60 text-white capitalize">{p.status}</span>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-brand font-serif text-lg">{formatPrice(p.price)}</span>
                  <span className="text-xs text-white/40">{p.region}</span>
                </div>
                <p className="text-white text-sm line-clamp-1 mb-3">{p.title}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setDefaultStatus(p.status || "active"); setEditing(p); setDialogOpen(true); }} data-testid={`edit-property-${p.id}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm border border-white/10 text-xs text-white/70 hover:text-brand hover:border-brand/40 transition-colors">
                    <PencilSimple size={14} /> Edit
                  </button>
                  {p.status !== "sold" && (
                    <button
                      type="button"
                      onClick={() => markSold(p)}
                      data-testid={`mark-sold-${p.id}`}
                      className="px-3 py-2 rounded-sm border border-white/10 text-xs text-white/50 hover:text-brand hover:border-brand/40"
                    >
                      Sold
                    </button>
                  )}
                  <button onClick={() => remove(p)} data-testid={`delete-property-${p.id}`} className="px-3 py-2 rounded-sm border border-white/10 text-xs text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PropertyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        onSaved={load}
        defaultStatus={defaultStatus}
      />
    </div>
  );
}
